import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import type { AppAlert } from '../lib/types'
import { IconBell } from './icons'
import { GhostMark } from './GhostMark'
import { alertText } from '../lib/alert'
import { timeAgo } from '../lib/format'
import './alerts.css'

// Notifications bell for the sidebar: an unread badge plus a dropdown of recent
// live alerts. Opening the panel marks everything read; clicking an alert opens
// that candidate.
export function AlertBell() {
  const { alerts, unreadAlerts, markAlertsRead, dismissAlert } = useStore()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)

  function toggle() {
    setOpen((o) => {
      if (!o) markAlertsRead()
      return !o
    })
  }

  function go(a: AppAlert) {
    setOpen(false)
    // Announcements aren't candidate-bound and don't deep-link — they're
    // informational in the bell (the banner is their actionable surface).
    if (a.kind === 'announcement' || !a.candidateId) return
    nav(`/candidates/${a.candidateId}`)
  }

  return (
    <div className="bell">
      <button
        type="button"
        className={`bell__btn${open ? ' is-open' : ''}`}
        onClick={toggle}
        aria-label={unreadAlerts ? `${unreadAlerts} new alerts` : 'Alerts'}
        aria-expanded={open}
      >
        <IconBell size={18} />
        {unreadAlerts > 0 && <span className="bell__badge">{unreadAlerts > 9 ? '9+' : unreadAlerts}</span>}
      </button>

      {open && (
        <>
          <div className="bell__scrim" onClick={() => setOpen(false)} aria-hidden />
          <div className="bell__panel" role="menu">
            <div className="bell__head">Alerts</div>
            {alerts.length === 0 ? (
              <div className="bell__empty">
                No alerts yet. You’ll be notified the moment a candidate passes a Live Vouch.
              </div>
            ) : (
              <ul className="bell__list">
                {alerts.map((a) => (
                  <li key={a.id} className="bell__item">
                    <button
                      type="button"
                      className="bell__item-main"
                      onClick={() => go(a)}
                      style={a.kind === 'announcement' ? { cursor: 'default' } : undefined}
                    >
                      <GhostMark size={22} tint={a.kind === 'announcement' ? 'vault' : 'live'} />
                      <span className="bell__item-text">
                        <span>
                          {a.kind === 'announcement' ? (
                            <b>{a.title}</b>
                          ) : (
                            <>
                              <b>{a.candidateName}</b> {alertText(a.kind)}
                            </>
                          )}
                        </span>
                        <small>{timeAgo(a.at)}</small>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="bell__dismiss"
                      aria-label="Dismiss"
                      onClick={() => dismissAlert(a.id)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  )
}
